import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SimpleChipOption {
  id: string;
  label: string;
  emoji: string;
}

interface SimpleChipSelectorProps {
  options: SimpleChipOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  mode?: 'single' | 'multiple';
  columns?: 2 | 3;
}

export const SimpleChipSelector: React.FC<SimpleChipSelectorProps> = ({
  options,
  selected,
  onChange,
  mode = 'multiple',
  columns = 3,
}) => {
  const handleToggle = (id: string) => {
    if (mode === 'single') {
      onChange(selected.includes(id) ? [] : [id]);
    } else {
      if (selected.includes(id)) {
        onChange(selected.filter((item) => item !== id));
      } else {
        onChange([...selected, id]);
      }
    }
  };

  return (
    <div className={cn(
      'grid gap-3',
      columns === 2 && 'grid-cols-2',
      columns === 3 && 'grid-cols-3',
    )}>
      {options.map((option) => {
        const isSelected = selected.includes(option.id);

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => handleToggle(option.id)}
            className={cn(
              'relative flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 min-h-[88px]',
              'hover:scale-105 active:scale-95',
              !isSelected && 'bg-muted/50 border-border text-muted-foreground hover:border-border/80',
              isSelected && 'bg-[#cfef00]/20 border-[#cfef00] text-foreground ring-1 ring-[#cfef00]',
            )}
          >
            {isSelected && (
              <div className="absolute top-2 right-2">
                <Check className="h-4 w-4 text-foreground" />
              </div>
            )}
            <span className={cn('text-2xl transition-opacity', isSelected ? 'opacity-100' : 'opacity-60')}>
              {option.emoji}
            </span>
            <span className={cn('text-xs font-medium text-center leading-tight', isSelected && 'font-semibold')}>
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};
